import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CreateTaskResult, RecordTypeFieldDefinition } from 'src/app/data/model/task';
import { TasksService } from 'src/app/data/service/tasks.service';

export interface RequestApprovalOptions {
  organizationId: string;
  /** RecordTypes.Code of the record, e.g. invoice. */
  recordType: string;
  recordId: string;
  /** Shown under the title, e.g. "Acme Supplies Ltd · INV-0042". */
  recordTitle?: string;
  /** An expense report's submitter, whose part of it is to be approved. */
  subjectUserId?: string;
  /** Start with these fields chosen instead of the whole record. */
  fieldKeys?: string[];
}

/**
 * Ask someone to approve a record, or just some of its fields -- the ones Admin > Record types
 * marks approvable, e.g. a bill's account and tax rate. Opened with TaskRequestService.open(),
 * which resolves with the created task, or null when dismissed.
 */
@Component({
  selector: 'app-request-task-modal',
  templateUrl: './request-task-modal.component.html',
  styleUrls: ['./request-task-modal.component.scss']
})
export class RequestTaskModalComponent implements OnInit {
  options: RequestApprovalOptions;

  recordTypeName = 'record';
  approvableFields: RecordTypeFieldDefinition[] = [];
  loadingFields = true;

  scope: 'whole' | 'fields' = 'whole';
  chosen = new Set<string>();
  assignee: string = null;
  description = '';
  dueDate = '';
  highPriority = false;

  submitted = false;
  saving = false;
  errorMessage: string;
  errorDetails: string[] = [];

  /** Earliest due date the picker offers: today, local time. */
  readonly today = RequestTaskModalComponent.localDate(new Date());

  private static localDate(d: Date): string {
    const pad = (n: number) => (n < 10 ? '0' : '') + n;
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  constructor(public activeModal: NgbActiveModal, private _tasks: TasksService) { }

  ngOnInit(): void {
    this._tasks.recordTypes().subscribe(
      types => {
        const type = (types || []).find(t => t.code === this.options?.recordType);
        this.recordTypeName = (type?.name || 'record').toLowerCase();
        this.approvableFields = (type?.fields || []).filter(f => f.isActive && f.isApprovable)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        const preset = (this.options?.fieldKeys || []).filter(k => this.approvableFields.some(f => f.key === k));
        if (preset.length) {
          this.scope = 'fields';
          preset.forEach(k => this.chosen.add(k));
        }
        this.loadingFields = false;
      },
      () => { this.loadingFields = false; });
  }

  get headerFields(): RecordTypeFieldDefinition[] {
    return this.approvableFields.filter(f => f.section === 'header');
  }

  get lineFields(): RecordTypeFieldDefinition[] {
    return this.approvableFields.filter(f => f.section === 'lines');
  }

  toggle(key: string): void {
    if (this.chosen.has(key)) { this.chosen.delete(key); } else { this.chosen.add(key); }
  }

  get noFieldsChosen(): boolean {
    return this.scope === 'fields' && this.chosen.size === 0;
  }

  get dueInPast(): boolean {
    return !!this.dueDate && this.dueDate < this.today;
  }

  submit(): void {
    this.submitted = true;
    if (this.saving || !this.assignee || this.noFieldsChosen || this.dueInPast) { return; }

    this.saving = true;
    this.errorMessage = null;
    this.errorDetails = [];

    const o = this.options;
    const role = this.assignee.startsWith('role:') ? this.assignee.substring(5) as 'admin' : undefined;
    this._tasks.create({
      organisationId: o.organizationId,
      recordType: o.recordType,
      recordId: o.recordId,
      subjectUserId: o.subjectUserId || undefined,
      fieldKeys: this.scope === 'fields' ? this.approvableFields.filter(f => this.chosen.has(f.key)).map(f => f.key) : [],
      description: this.description.trim() || undefined,
      assignedToUserId: role ? undefined : this.assignee,
      assignToRole: role,
      dueDate: this.dueDate || undefined,
      priority: this.highPriority ? 'high' : 'normal'
    }).subscribe(
      (result: CreateTaskResult) => {
        this.saving = false;
        this._tasks.requestRefresh();
        this.activeModal.close(result);
      },
      error => {
        this.saving = false;
        const { message, details } = TasksService.errorMessage(error, 'The request could not be sent.');
        this.errorMessage = message;
        this.errorDetails = details;
      });
  }

  cancel(): void {
    if (!this.saving) { this.activeModal.dismiss(); }
  }
}
